import styled from "styled-components"
import { StyledLinkContent } from "../../global-header/partials/StyledLink"

export interface HeroProps {
  dark_form?: boolean
  is_form?: boolean
  hover_icon?: boolean
  arrow?: boolean
}

export const StyledHero = styled.div`
  box-shadow: 0 0 16px ${(props) => props.theme.colors.darkGrey10};
  background-color: ${(props) => props.theme.colors.white};
  width: 100%;
`
export const StyledHeroContainer = styled.div<HeroProps>`
  ${(props) => props.theme.mixins.container}
  @media (min-width: ${(props) => props.theme.breakPoints.tablet}) {
    min-height: 540px;
    max-height: ${(props) => (props.is_form ? "auto" : "768px")};
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-column-gap: 64px;
    ${(props) =>
      props.is_form
        ? `
      position: relative;
      grid-template-columns: repeat(2,1fr);
    `
        : `
      grid-template-columns: repeat(12,1fr);
    `}
  }
  @media (max-width: ${(props) => props.theme.breakPoints.desktop}) {
    grid-column-gap: 48px;
  }
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    display: flex;
    flex-direction: column;
    padding: 0;
  }
`
export const StyledHeroContent = styled.div<HeroProps>`
  ${(props) =>
    props.is_form
      ? `
    grid-column: 1;
    position: relative;
    z-index: 1;
  `
      : `
    grid-column-start: 1;
    grid-column-end: 5;
  `}
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  padding: 48px 0;
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    padding: 32px 24px;
    order: 1;
  }
  @media (max-width: ${(props) => props.theme.breakPoints.mobile}) {
    padding: 24px 16px;
  }

  & h1 {
    ${(props) => props.theme.mixins.h1}
    &:after {
      ${(props) => props.theme.mixins.headingAccent};
    }
  }
`
export const StyledHeroLogo = styled.div`
  max-width: 132px;
  position: relative;
  margin-bottom: 24px;

  & > a {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
  }
  & img {
    object-fit: initial;
  }
`
export const StyledHeroBreadcrumbs = styled.div`
  margin: 0 0 8px;

  & a {
    ${(props) => props.theme.mixins.label};
    color: ${(props) => props.theme.colors.darkGrey60};
    margin-left: 4px;

    &:before {
      content: "/";
      margin-right: 4px;
    }
    &:nth-of-type(1) {
      margin-left: 0;

      &:before {
        display: none;
      }
    }
  }
`
export const StyledHeroDescription = styled.div`
  ${(props) => props.theme.mixins.markdown}
  margin-top: 16px;
`
export const StyledHeroButtons = styled.div`
  margin: 24px 0 0;
  display: flex;
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    margin: 16px 0 0;
  }

  & .button-v2--text {
    padding-top: 0;
    padding-bottom: 0;
    min-height: 0;
  }
`
export const StyledHeroImage = styled.div`
  grid-column-start: 5;
  grid-column-end: 13;
  display: flex;
  height: 100%;
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    order: 0;
  }

  & > div {
    width: 100%;
    height: 100%;
    @media (min-width: ${(props) => props.theme.breakPoints.tablet}) {
      aspect-ratio: 3 / 2;
    }
    @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
      aspect-ratio: 16 / 9;
    }

    & img,
    & video {
      width: 100%;
      object-fit: cover;
      height: 100%;
    }
  }
`

// FORM

export const StyledHeroForm = styled.div`
  grid-column: 2;
  display: flex;
  height: 100%;
  padding: 48px 0;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    order: 2;
    padding: 0;
  }
`
export const StyledHeroFormInner = styled.div<HeroProps>`
  padding: 48px;
  width: 100%;
  position: relative;
  background: ${(props) =>
    props.dark_form ? props.theme.colors.darkGrey : props.theme.colors.white};
  box-shadow: 0 0 16px ${(props) => props.theme.colors.darkGrey05};
  border: 1px solid ${(props) => props.theme.colors.darkGrey05};
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    padding: 32px 24px;
  }
  @media (max-width: ${(props) => props.theme.breakPoints.mobile}) {
    padding: 24px 16px;
  }

  ${(props) =>
    props.dark_form &&
    props.arrow &&
    `
    &:before{
      content: "";
      position: absolute;
      top: 50%;
      left: -8px;
      transform: translate(0, -50%);
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 24px 0 24px 24px;
      border-color: transparent transparent transparent ${props.theme.colors.white};
      @media (max-width: ${props.theme.breakPoints.tablet}) {
        top: -8px;
        left: 50%;
        transform: translate(-50%, 0);
        border-width: 24px 24px 0 24px;
        border-color: ${props.theme.colors.white} transparent transparent transparent;
      }
    }`}
`
export const StyledHeroFormTitle = styled.p<HeroProps>`
  ${(props) => props.theme.mixins.h2}
  ${(props) => props.dark_form && `color: ${props.theme.colors.white};`}
  padding-bottom: 8px;
`
export const StyledHeroFormCopy = styled.div<HeroProps>`
  & p {
    ${(props) => props.theme.mixins.body}
    ${(props) => props.dark_form && `color: ${props.theme.colors.white80true};`}

    & a {
      color: ${(props) => props.theme.colors.white};
      text-decoration: underline;
    }
  }
  padding-bottom: 16px;
`
export const StyledHeroFormImage = styled.div`
  position: absolute;
  top: 0;
  right: -32px;
  width: calc(100% + 64px);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    top: -12px;
    right: 0;
    width: 100%;
    height: 100%;
  }
  @media (min-width: ${(props) => props.theme.breakPoints.tablet}) {
    & > div {
      width: 100%;
    }
  }
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    & > div {
      height: 100%;
    }
  }
`

// HIGHLIGHT
export interface StyledHeroHighlightImageProps {
  image: string
}
export const StyledHeroHighlightImage = styled.div<StyledHeroHighlightImageProps>`
  grid-column-start: 5;
  grid-column-end: 13;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-image: url(${(props) => props.image});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    order: 2;
  }
`
export const StyledHeroHighlight = styled.div`
  width: 100%;
  @media (min-width: ${(props) => props.theme.breakPoints.tablet}) {
    max-width: 480px;
  }
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    padding: 24px;
  }
  @media (max-width: ${(props) => props.theme.breakPoints.mobile}) {
    padding: 24px 16px;
  }
`

// FULL BLEED

export interface StyledHeroFullBleedProps {
  bg_image: string
}

export const StyledHeroFullBleed = styled.div<StyledHeroFullBleedProps>`
  width: 100%;
  height: 100vh;
  max-height: 768px;
  background-color: ${(props) => props.theme.colors.darkGrey};
  margin-top: -121px;
  padding-top: 121px;
  display: flex;
  align-items: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  background-image: url("${(props) => props.bg_image}");
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    margin-top: -99px;
    padding-top: 99px;
  }
`
export const StyledHeroFullBleedContainer = styled.div`
  ${(props) => props.theme.mixins.container}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`
export const StyledHeroFullBleedTitle = styled.h1`
  ${(props) => props.theme.mixins.h1}
  font-size: 76px;
  line-height: 80px;
  color: ${(props) => props.theme.colors.white};
  text-align: center;
  margin: 0;
  @media (max-width: ${(props) => props.theme.breakPoints.desktop}) {
    font-size: 48px;
    line-height: 52px;
  }
`
export const StyledHeroFullBleedDescription = styled.div`
  ${(props) => props.theme.mixins.markdownAlt}
  text-align: center;
  margin: 16px 0 0;

  & p,
  & strong,
  & b {
    font-size: 18px;
    line-height: 22px;
    color: ${(props) => props.theme.colors.white};

    @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
      font-size: 16px;
      line-height: 20px;
    }
  }
`
export const StyledHeroFullBleedButtons = styled.div`
  margin: 24px 0 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  @media (max-width: ${(props) => props.theme.breakPoints.tablet}) {
    margin: 16px 0 0;
  }

  & div,
  & a {
    margin: 8px;
  }

  & .button-v2--text {
    padding-top: 0;
    padding-bottom: 0;
    min-height: 0;
  }
`

export const StyledProgramLogoWrapper = styled.div`
  padding: 16px 0 21px 0;
  width: fit-content;
`
export const StyledProgramLogo = styled.div<HeroProps>`
  position: relative;

  ${StyledLinkContent} {
    img {
      height: 26px;
      padding: 0;
    }
  }
`
